<script setup lang="ts">
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import type { EditorNode } from "@/components/form-builder/types"
import { useFormBuilder } from "@/components/form-builder/useFormBuilder"
import CheckboxToggle from "@/components/form/CheckboxToggle.vue"
import { useLocale } from "@/composables/useLocale"
import type { ContactTopic } from "@/model/communication/contact-topic"
import type { ContactTopicGroup } from "@/model/communication/contact-topic-group"
import { useApp, useAppConfig, useHttpClient } from "@/vf"
import { FormKitSchema } from "@formkit/vue"
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"

const props = defineProps<{
    element: any
}>()

const emit = defineEmits<{
    (e: "submitted"): void
}>()

const { appConfig } = useAppConfig()
const { apiLink } = useApp()
const http = useHttpClient()
const { locale } = useLocale()
const { t } = useI18n()
const acceptTerms = ref(false)

const fb = useFormBuilder([])
const hasCustomSchema = computed(() => fb.nodes.value.length > 0)
const customSchema = computed(() => {
    const guestFields = !appConfig.value.account
        ? [
              { $formkit: "text", name: "firstName", label: t("@cms:contactForm.firstName"), validation: "required" },
              { $formkit: "text", name: "lastName", label: t("@cms:contactForm.lastName"), validation: "required" },
              { $formkit: "email", name: "email", label: t("@cms:contactForm.email"), validation: "required|email" },
          ]
        : []
    return [...guestFields, ...fb.toSchema()]
})

const issueName = ref("")

function injectTopicOptions(nodes: EditorNode[], topics: ContactTopic[]) {
    for (const node of nodes) {
        if (node.type === "topic") {
            // an empty selection means the form was never customized — fall back to all topics of the group
            const selected = node.options.length
                ? node.options
                      .map(opt => topics.find(topic => topic.id === opt.value))
                      .filter((topic): topic is ContactTopic => !!topic)
                : topics
            node.options = selected.map(topic => ({ label: topic.name ?? "", value: topic.id ?? "" }))
        }
        if (node.type === "layout") {
            injectTopicOptions(node.children, topics)
        }
    }
}

onMounted(async () => {
    const topicGroup = props.element.config?.contactTopicGroup
    if (!topicGroup?.id) {
        return
    }

    const { data: group } = await http.get<ContactTopicGroup>(`/communication-messages/topic-group/${topicGroup.id}`)
    const translation = group.translations?.find(tr => tr.language === locale.value) ?? group.translations?.[0]
    issueName.value = translation?.issueName ?? translation?.name ?? ""
    if (translation?.formSchema && Array.isArray(translation.formSchema)) {
        fb.nodes.value = translation.formSchema as EditorNode[]
        injectTopicOptions(fb.nodes.value, group.topics ?? [])
    }
})

function checkSubmit(): boolean {
    return appConfig.value.login || acceptTerms.value
}

function flattenNodes(nodes: EditorNode[]): EditorNode[] {
    return nodes.flatMap(n => (n.type === "layout" ? flattenNodes(n.children) : [n]))
}

function resolveFieldValue(n: EditorNode, data: Record<string, any>): unknown {
    return n.options?.length ? n.options.find(o => o.value === String(data[n.name]))?.label ?? data[n.name] : data[n.name]
}

async function submitCustomForm(data: Record<string, any>) {
    if (!checkSubmit()) {
        return
    }

    const f = new FormData()
    const guestKeys = new Set<string>()

    if (!appConfig.value.account) {
        f.append("firstName", data.firstName ?? "")
        f.append("lastName", data.lastName ?? "")
        f.append("email", data.email ?? "")
        guestKeys.add("firstName")
        guestKeys.add("lastName")
        guestKeys.add("email")
    }

    const flatNodes = flattenNodes(fb.nodes.value)

    const rows = flatNodes
        .filter(
            n =>
                n.type !== "submit" &&
                n.type !== "upload" &&
                !guestKeys.has(n.name) &&
                data[n.name] != null &&
                data[n.name] !== "",
        )
        .map(n => {
            const label = n.label || n.name
            const value = resolveFieldValue(n, data)
            return `<tr><th style="text-align:left;padding:4px 12px 4px 0;white-space:nowrap">${label}</th><td style="padding:4px 0">${String(
                value ?? "",
            )}</td></tr>`
        })

    f.append("text", `<table>${rows.join("")}</table>`)
    f.append("subject", issueName.value)
    f.append("contactTopicGroupId", props.element.config?.contactTopicGroup?.id ?? "")

    const topicNode = flatNodes.find(n => n.type === "topic")
    if (topicNode && data[topicNode.name]) {
        f.append("topicId", data[topicNode.name])
    }

    const subtitleNode = flatNodes.find(n => n.useAsIssueSubtitle && data[n.name] != null && data[n.name] !== "")
    if (subtitleNode) {
        f.append("issueSubtitle", String(resolveFieldValue(subtitleNode, data) ?? ""))
    }

    for (const n of flatNodes.filter(n => n.type === "upload")) {
        const files = (data[n.name] ?? []) as File[]
        files.forEach((file, index) => {
            f.append(`attachments[${n.name}_${index}]`, file, file.name)
        })
    }

    await http.post("/communication-messages/", f)
    emit("submitted")
}

defineExpose({ hasCustomSchema })
</script>

<template>
    <div class="cms-admin-element-body">
        <template v-if="hasCustomSchema">
            <FormKit type="form" @submit="submitCustomForm" :actions="false">
                <FormKitSchema :schema="customSchema" />

                <div class="checkbox" v-if="!appConfig.login">
                    <CheckboxToggle v-model="acceptTerms">
                        <VarsityContentProcessor
                            :content="
                                $t('@cms:contactForm.acceptTerms', { link: apiLink('/legal/show/DataPrivacy/pdf') })
                            "
                            allow-all-tags
                        />
                    </CheckboxToggle>
                </div>
                <div class="text-right d-block">
                    <button type="submit" class="btn btn-primary" :disabled="!appConfig.login && !acceptTerms">
                        {{ $t("@cms:contactForm.submit") }}
                    </button>
                </div>
            </FormKit>
        </template>
    </div>
</template>
